// Default rate schedule for the Billing tab's Rates & Rules view, transcribed
// from "VoltaGrid Block 4 & 5 Rate Schedule 2026.08.31" (2026 All Inclusive)
// and reconciled 2026-09-09 against the rate buildup workbook ("Rate Buildup -
// Working File 2026 - Strike - 2026.08.31-Eleah", Bid/No Bid flags on the
// Labor-SW GC ETX and EQUIP - SW sheets): only Bid-flagged rows are carried —
// the big No Bid crawler cranes came out, the Cathodic Protection section and
// a handful of other Bid rows came in, and three equipment rates that had
// been transcribed wrong were corrected to the workbook's South Central rates.
// These are the seed values every job starts from; per-job edits are stored as
// Forecast_User_Data overrides (see billingRates.js) so this file never needs
// to change when a rate is negotiated — only when the company-wide template
// document itself is replaced.
//
// Row ids hash the row's default content, so reordering or appending rows here
// is safe. Editing a default row's text in this file orphans any stored
// override for it (the override keys off the old content hash) — treat rows
// as append-only.

// djb2 — same hash billing.js uses for transaction keys.
const hash = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return h.toString(36);
};

// Billable rate math from the schedule: ST = wage × multiplier, OT = ST × otf.
export const DEFAULT_RATE_CONFIG = { m: 1.633, otf: 1.4 };

export const UOM_LABELS = { hr: 'per hour', day: 'per day', ea: 'each' };

// ── Schedule of Labor Rates ──
// [classification, target base hourly wage, note?]
const LABOR = {
    'Safety & Quality Control': [
        ['Sr. Quality Manager', 75],
        ['Quality Manager', 70],
        ['Quality Control Inspector I', 65],
        ['Quality Control Inspector II', 62],
        ['Quality Control Inspector III', 58],
        ['Quality Control Inspector IV', 55],
        ['Safety Manager', 75],
        ['Safety Specialist', 65],
        ['HSE Specialist', 65],
        ['First Aid & Medical Support', 75],
    ],
    'Program Management': [
        ['Sr. Program Manager', 65],
        ['Program Manager', 60],
        ['Assistant Program Manager', 57],
        ['Program Specialist', 50],
        ['Sr. Program Coordinator', 35],
        ['Program Coordinator', 32],
    ],
    'Project Management': [
        ['Project Director', 125],
        ['Senior Project Manager', 115],
        ['Project Manager', 110],
        ['Assistant Project Manager', 65],
        ['Construction Manager', 100],
    ],
    'Project Controls': [
        ['Project Controls Manager', 85],
        ['Project Coordinator', 45],
        ['Sr. Planner', 75],
        ['Planner', 65],
        ['Sr. Cost Controller', 75],
        ['Cost Controller', 65],
        ['Project Admin Support Specialist', 35],
        ['Scheduling Manager', 85],
        ['Project Scheduler', 80],
    ],
    Procurement: [
        ['Procurement Manager', 75],
        ['Procurement Specialist', 65],
        ['Project Field Materials Coordinator', 55],
        ['Supply Chain Manager', 75],
        ['Material Buyer/Expeditor', 45],
        ['Material Receiving Clerk', 35],
    ],
    'Document Control & Administrative': [
        ['Document Control Manager', 45],
        ['Document Control Specialist', 35],
        ['IT and Communications Support', 50],
        ['Office Manager', 50],
    ],
    'Facility Construction': [
        ['Site Manager', 125],
        ['Superintendent - General', 90],
        ['Millwright Superintendent', 85],
        ['Ironworker Superintendent', 80],
        ['Piping Superintendent', 80],
        ['Rigging Superintendent', 80],
        ['Assistant Superintendent', 65],
        ['Foreman - General', 60],
        ['Foreman - Craft', 52],
        ['Foreman - Labor', 50],
        ['Foreman - Welding (w/o Rig)', 60],
        ['Welder - In-Service (w/o Rig)', 48],
        ['TIG Welder - Certified (w/o Rig)', 50],
        ['Welder - Certified (w/o Rig)', 48],
        ['Heavy Crane Operator', 60],
        ['Crane Operator', 55],
        ['Rigger', 45],
        ['Equipment Operator - A', 45],
        ['Equipment Operator - B', 40],
        ['Equipment Operator - C', 35],
        ['Truck Driver - A (Equipment Operator w/ CDL)', 42],
        ['Truck Driver - B', 40],
        ['Millwright', 75],
        ['Ironworker', 45],
        ['Craftsman - A', 45, 'Includes Pumper, Carpenter, Pipefitter, Finisher'],
        ['Craftsman - B', 43, 'Includes Pumper, Carpenter, Pipefitter, Finisher'],
        ['Craftsman - C', 40, 'Includes Pumper, Carpenter, Pipefitter, Finisher'],
        ['Craft Helper - A', 31, 'Includes Welder Helper & Mud Technician'],
        ['Craft Helper - B', 27, 'Includes Welder Helper & Mud Technician'],
        ['Skilled Laborer', 26.5],
        ['Common Laborer', 24.25],
        ['Bending Engineer', 45],
        ['Mechanic', 40],
        ['Mechanic Helper', 32],
        ['Line Locator', 40],
    ],
    'Instrumentation & Electrical': [
        ['I&E Project Manager', 90],
        ['Electrical Construction Manager', 90],
        ['I&E Superintendent', 80],
        ['I&E Foreman', 55],
        ['Journeyman Electrician', 50],
        ['Electrician Apprentice 3rd / 4th year', 37],
        ['Electrician Apprentice 1st / 2nd year', 33],
        ['Instrument Fitter', 50],
        ['Instrument Fitter Helper', 37],
        ['Instrument Technician', 65],
        ['Electrical Equipment Operator', 41],
    ],
    Engineering: [
        ['Field Engineer I', 99],
        ['Field Engineer II', 92],
        ['Field Engineer III', 85],
        ['Field Engineer IV', 75],
        ['Mechanical Project Engineer', 92],
        ['Electrical Project Engineer', 92],
        ['I&E Specialist', 73],
    ],
};

// ── Schedule of Equipment Rates ──
// [description, notes & applicable models, rate, uom]
const EQUIP = {
    'Road Trucks': [
        ['Truck - 1/2 Ton', 'Includes up to 5 Non-Motorized Tools <$250ea', 21.93, 'hr'],
        ['Truck - 3/4 Ton', 'Includes up to 5 Non-Motorized Tools <$250ea', 30.43, 'hr'],
        ['Truck - 1 Ton (Single Rear Wheel)', 'Includes up to 5 Non-Motorized Tools <$250ea', 34.02, 'hr'],
        ['Truck - 1 Ton (Double Rear Wheel)', 'Includes up to 5 Non-Motorized Tools <$250ea', 35.06, 'hr'],
        ['Truck - 1 Ton w/Autocrane', 'Includes up to 5 Non-Motorized Tools <$250ea', 53.42, 'hr'],
        ['Truck - 1 Ton Mechanic Rig', 'Includes Air Compressor, Hydraulic Jacks, Tools etc', 57.50, 'hr'],
        ['Truck - 1.5 Ton Mechanic Rig', 'Includes Air Compressor, Hydraulic Jacks, Tools etc', 57.50, 'hr'],
        ['Truck - 1 Ton (w/ Dump Bed)', '', 42.52, 'hr'],
        ['Truck - 1 Ton (Weld Rig) Rod/Stick', 'Includes Normal Welding Rods, Grinding Disks, & Oxygen/Acetylene', 32.86, 'hr'],
        ['Truck - 1 Ton (Weld Rig) TIG', 'Includes Normal Welding Rods, Grinding Disks, & Oxygen/Acetylene', 41.43, 'hr'],
        ['Truck - 1 1/2 Ton (Double Rear Wheel)', '', 35.52, 'hr'],
        ['Truck - 2 - 3 Ton Flatbed', 'Equipment Service truck includes Air Compressor and Fuel & Lube Equipment', 45.90, 'hr'],
        ['Truck - 1.5 Ton w/Autocrane', '', 57.50, 'hr'],
        ['Truck - 10 Ton Haul - Tandem Axle', '', 98.82, 'hr'],
        ['Truck - 10 Ton Haul - Three Axle', '', 101.07, 'hr'],
        ['Truck - 2,500 Gallon Fuel', 'Peterbilt L0006', 94.35, 'hr'],
        ['Truck - 2,000 Gallon Water Tanker', '', 104.42, 'hr'],
        ['Truck - 4,000 Gallon Water Tanker', '', 84.21, 'hr'],
        ['Truck - 8,000 Gallon Water Tanker', '', 334.82, 'hr'],
        ['Truck - 12 Yd Tandem Axle Dump', '', 99.14, 'hr'],
        ['Truck - Fuel & Lube', '', 90.74, 'hr'],
        ['Bus - 15 Passenger Van', '', 469.48, 'day'],
    ],
    Trailers: [
        ["Trailer - 16' Open Side Cooling Trailer", 'Includes Port-a-Cool & Generator', 240.00, 'day'],
        ['Trailer - 40 - 50 Ton Lowboy', '', 312.50, 'day'],
        ['Trailer - 55 Ton Lowboy w/ Flip Axle', '', 551.24, 'day'],
        ['Trailer - Bumper Pull Utility', "Includes 16' trailer", 86.35, 'day'],
        ['Trailer - Pipe - Bumper Pull', "Includes 36 - 40' pipe", 162.00, 'day'],
        ['Trailer - Drop Deck Float', '', 266.67, 'day'],
        ['Trailer - FlexSteel', '', 613.73, 'day'],
        ['Trailer - Float', "48 to 53' float and may include dolly", 195.84, 'day'],
        ['Trailer - 5th Wheel Pipe Stringing Rollagon', '', 800.00, 'day'],
        ['Trailer - Gooseneck Tandem Axle', '', 124.38, 'day'],
        ['Trailer - Poly', 'Includes Spool Trailer (hydraulic or manual)', 120.00, 'day'],
        ['Trailer - Water', '1,000-1,600 Gallon capacity (Water Buffalo)', 368.31, 'day'],
        ['Trailer - Water', '300-500 Gallon capacity (Water Buffalo)', 269.70, 'day'],
    ],
    'Off Road Vehicles': [
        ['ADT - 30 Ton', 'Cat 730', 208.93, 'hr'],
        ['ADT - 40 Ton', 'Cat 740', 258.75, 'hr'],
        ['Scraper - 23 yd', 'Cat 623, Cat 627 or equivalent', 319.69, 'hr'],
        ['ATV - 4 Wheel', '', 240.88, 'day'],
    ],
    Excavators: [
        ['Rubber Tire Backhoe (Machine with Standard Bucket)', 'JD 310, Cat 416, Cat 420/430 or Equivalent', 41.89, 'hr'],
        ['Attachment - Backhoe Hammer', '', 409.41, 'day'],
        ['Excavator - 2 to 6 Metric Ton (Machine with Standard Bucket)', 'JD 50, Cat 304, Komatsu PC50 or Equivalent', 41.20, 'hr'],
        ['Excavator - 7 to 10 Metric Ton (Machine with Standard Bucket)', 'JD 80, Cat 307, Cat 308, Komatsu PC78 or Equivalent', 58.24, 'hr'],
        ['Excavator - 11 to 14 Metric Ton (Machine with Standard Bucket)', 'JD 120, Cat 312, Cat 314, Komatsu PC120 or Equivalent', 78.27, 'hr'],
        ['Excavator - 15 to 19 Metric Ton (Machine with Standard Bucket)', 'JD 160, Cat 315, Cat 316, Komatsu PC170 or Equivalent', 86.13, 'hr'],
        ['Attachment - Excavator 2,000 lb Hammer', '', 1200.00, 'day'],
        ['Attachment - Excavator 3,500 lb Hammer', '', 1300.00, 'day'],
        ['Attachment - Excavator 6,500 lb Hammer', '', 1660.00, 'day'],
        ['Attachment - Excavator 7,500 lb Hammer', '', 1800.00, 'day'],
        ['Attachment - Excavator 10,000 lb Hammer', '', 2222.00, 'day'],
        ['Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)', 'JD 200, JD 240, Cat 320, Cat 324, Komatsu PC210 or Equivalent', 85.32, 'hr'],
        ['Attachment - Vacuum Pipe Lift', '20-24 Metric Ton Excv', 1103.90, 'day'],
        ['Excavator - 25 to 29 Metric Ton (Machine with Standard Bucket)', 'JD 250, JD 270, Cat 329, Komatsu PC290 or Equivalent', 108.95, 'hr'],
        ['Attachment - Excavator Vacuum Pipe Lift', '25-29 Metric Ton Excv', 1329.60, 'day'],
        ['Attachment - Excavator Padding Bucket', '25-29 Metric Ton Excv', 1840.00, 'day'],
        ['Attachment - Excavator Padding Bucket', '30-36 Metric Ton Excv', 2100.00, 'day'],
        ['Excavator - 30 to 36 Metric Ton (Machine with Standard Bucket)', 'JD 350, Cat 336, Komatsu PC360 or Equivalent', 113.89, 'hr'],
        ['Excavator - 30 to 36 Metric Ton - Long Reach (Machine with Standard Bucket)', 'JD 350G LC, Cat 340 LRE, Komatsu PC360LC-11 SLF or Equivalent', 99.40, 'hr'],
        ['Attachment - Excavator Vacuum Pipe Lift', '30-36 Metric Ton Excv', 1666.67, 'day'],
        ['Excavator - 37 to 45 Metric Ton (Machine with Standard Bucket)', 'JD 380 P-Tier, Cat 340 / 349, Komatsu PC360LC / PC390LC / PC400 / PC450 or Equivalent', 176.95, 'hr'],
        ['Attachment - Excavator Vacuum Pipe Lift', '37-45 Metric Ton Excv', 1944.44, 'day'],
    ],
    'Dozers / Motorgraders / Compactors': [
        ['Dozer - 70 to 85 hp', 'JD 450J, JD 550J, Cat D4, Komatsu D31 or Equivalent', 111.27, 'hr'],
        ['Dozer - 86 to 110 hp', 'JD 650J, Cat D5, Komatsu D41 or Equivalent', 122.14, 'hr'],
        ['Dozer - 111 to 160 hp', 'JD 700J, JD 750J, Cat D6N, Komatsu D51 or Equivalent', 114.64, 'hr'],
        ['Dozer - 161 to 210 hp', 'JD 850J, Cat D6T, Cat D6R, Komatsu D65 or Equivalent', 152.50, 'hr'],
        ['Dozer - 211 to 280 hp', 'JD 950J-K, Cat D7, Komatsu D85 or Equivalent', 199.26, 'hr'],
        ['Dozer - 300 to 350 hp', 'JD 1050k, Cat D8T or Equivalent', 346.02, 'hr'],
        ['Motor Grader - 125 to 145 hp', 'Cat 120M, Cat 120M2, Komatsu GD555, Komatsu GD655 or Equivalent', 78.04, 'hr'],
        ['Motor Grader - 146 to 180 hp', 'Cat 12M3 or Equivalent', 125.54, 'hr'],
        ['Motor Grader - 180 to 212 hp', 'JD 670, JD 770, Cat 140M, Komatsu GD825 or Equivalent', 141.48, 'hr'],
        ['Motor Grader - 213 to 248 hp', 'JD 672, JD 772, JD 870, Cat 140M3, Cat 160M, Cat 160M3 or Equivalent', 181.48, 'hr'],
        ['Motor Grader - 250 to 300 hp', 'JD 872 or Equivalent', 208.75, 'hr'],
        ['Compactor - Walk Behind', 'Wacker Vibratory Rammer, Jumping Jack, Vibratory Plate Compactor or Equivalent', 21.99, 'hr'],
        ['Compactor - 66" Smooth/Pad Foot Roller', 'IR SD70, Sakai SV400, Bomag BW177 or Equivalent', 71.36, 'hr'],
        ['Compactor - 54" Smooth/Pad Foot Roller', 'HAMM 3205 or Equivalent', 76.09, 'hr'],
        ['Compactor - 84" Smooth/Pad Foot Roller', 'IR SD100, Sakai SV510, Bomag BW213 or Equivalent', 92.18, 'hr'],
        ['Compactor Ride On, 47"', '', 65.85, 'hr'],
        ['Trench Roller Compactor', '', 71.22, 'hr'],
    ],
    Cranes: [
        ['Crane - 15-17 Ton Rough Terrain', '', 78.57, 'hr'],
        ['Crane - 18-21 Ton Rough Terrain', '', 108.75, 'hr'],
        ['Crane - 22-25 Ton Rough Terrain', 'Grove 522C or Equivalent', 114.11, 'hr'],
        ['Crane - 26-28 Ton Rough Terrain', 'Grove 528C or Equivalent', 140.79, 'hr'],
        ['Crane - 29-39 Ton Rough Terrain', 'Grove 635C or Equivalent', 151.07, 'hr'],
        ['Crane - 40-49 Ton Rough Terrain', '', 163.35, 'hr'],
        ['Crane - 50-59 Ton Rough Terrain', 'Grove 750 or Equivalent', 203.42, 'hr'],
        ['Crane - 60-69 Ton Rough Terrain', 'Grove 865B or Equivalent', 210.43, 'hr'],
        ['Crane - 70-79 Ton Rough Terrain', 'Grove 870B or Equivalent', 256.10, 'hr'],
        ['Crane - 80-90 Ton Rough Terrain', 'Terex RT780 or Equivalent', 258.11, 'hr'],
        ['Crane - 91-100 Ton Rough Terrain', 'Grove RT890E or Equivalent', 300.45, 'hr'],
        ['Crane - 101-130 Ton Rough Terrain', 'Terex RT1120 or Equivalent', 376.79, 'hr'],
        ['Crane - 131-150 Ton Rough Terrain', 'Grove RT9150E or Equivalent', 466.96, 'hr'],
        ['Crane - 55 Ton Telescoping Crawler', 'Grove GHC 55, Matis 10010 or Equivalent', 268.75, 'hr'],
        ['Crane - 75 Ton Telescoping Crawler', 'Grove GHC 75, Mantis 15010 or Equivalent', 302.34, 'hr'],
        ['Crane - 130 Ton Telescoping Crawler', 'Grove GHC 130, Matis GTC-1200 or Equivalent', 420.90, 'hr'],
        ['Crane - 110 Ton Boom Crawler', 'Does not include mob/demob & assembly to be rebilled', 228.65, 'hr'],
        ['Crane - 150 Ton Boom Crawler', 'Does not include mob/demob & assembly to be rebilled', 299.51, 'hr'],
    ],
    'Forklifts & Man Lifts': [
        ['Forklift - Rough Terrain (non telehandler) - < 8k lb Cap.', '', 430.00, 'day'],
        ['Forklift - Telehandler - 8k to 9k lb Cap.', 'IR VR1044, IR VR1056 or Equivalent', 762.94, 'day'],
        ['Forklift - Telehandler - 10k to 12k lb Cap.', 'IR VR1044, IR VR1056 or Equivalent', 694.29, 'day'],
        ['Forklift - Telehandler - 14k lb Cap.', '', 1020.67, 'day'],
        ['Forklift - Telehandler - 15k lb Cap.', '', 1079.70, 'day'],
        ['Forklift - Telehandler - 16-17k lb Cap.', '', 1196.23, 'day'],
        ['Forklift - Telehandler - 20k lb Cap.', '', 164.80, 'hr'],
        ['Forklift - Telehandler - 26-30k lb Cap.', '', 1377.54, 'day'],
        ['Forklift - Telehandler - 35k lb Cap.', '', 1642.83, 'day'],
        ["Man lift - 50' or less, Telescopic Boom", '', 308.43, 'day'],
        ["Man lift - 60' - 65' Lift - Telescopic", '', 394.05, 'day'],
        ["Man lift - 85' Lift - Telescopic", '', 571.21, 'day'],
        ["Man lift - 125' Lift - Telescopic", '', 132.51, 'hr'],
        ["Man lift - 25' - 39' Scissor Lift", 'JLG 260MRT, Genie 2668RT, Genie 3268RT or Equivalent', 284.76, 'day'],
        ["Man lift - 40' - 54' Scissor Lift", 'JLG 4394RT, JLG 500RTS or Equivalent', 364.29, 'day'],
        ["Man lift - 30' - 44' - Articulating Boom Lift", 'Genie TMZ-34 or Equivalent', 275.71, 'day'],
        ["Man lift - 45' - 59' - Articulating Boom Lift", 'Genie Z/S-45, JLG 450A or Equivalent', 347.55, 'day'],
        ["Man lift - 60' - 74' - Articulating Boom Lift", 'Genie Z/S-60, JLG 600A or Equivalent', 422.57, 'day'],
        ["Man lift - 80' - Articulating Boom Lift", '', 520.57, 'day'],
        ["Man lift - 120' - Articulating Boom Lift", '', 1050.69, 'day'],
        ["Man lift - 135' Lift", '', 138.21, 'hr'],
    ],
    'Front End Loaders': [
        ['Skid Steer Mini Loader', '', 55.52, 'hr'],
        ['Skid Steer - >8K', 'Takeuchi TL8 or similar', 75.56, 'hr'],
        ['Attachment - Post Hole Digger (for Skid Steer Mini Loader)', 'Attachments would include Post Hole Digger, Broom, Grapple etc', 160.00, 'day'],
        ['Attachment - Broom (for Skid Steer Mini Loader)', '', 175.20, 'day'],
        ['Attachment - Mower Deck (for Skid Steer Mini Loader)', '', 112.50, 'day'],
        ['Attachment - Grapple (for Skid Steer Mini Loader)', '', 160.00, 'day'],
        ['Attachment - Auger w/Bit (for Skid Steer)', '', 160.00, 'day'],
        ['Attachment - Mulching Head (for Skid Steer)', '', 197.70, 'day'],
        ['Front End Loader - 2.5yd', 'JD444, Cat 928 or Equivalent', 106.88, 'hr'],
        ['Front End Loader - 3yd', 'JD 544, Cat 938 or Equivalent', 104.00, 'hr'],
        ['Front End Loader - 4yd', 'JD 624, Cat 950 or Equivalent', 125.00, 'hr'],
        ['Front End Loader - 4.5yd', 'JD 644, Cat 966 or Equivalent', 157.32, 'hr'],
        ['Front End Loader - 5yd', 'JD 744, Cat 980 or Equivalent', 197.68, 'hr'],
        ['Front End Loader - 5.5yd', 'Cat 972 or Equivalent', 191.25, 'hr'],
        ['Front End Loader - 6yd', 'Cat 980 or Equivalent', 201.96, 'hr'],
        ['Front End Loader - 6.5yd', 'Cat 988 or Equivalent', 361.79, 'hr'],
    ],
    'Pipelayers / Sidebooms': [
        ['Pipelayer - 10,000 lb Sideboom', 'JD 450J, Cat D3, RT Backhoe or Equivalent', 64.14, 'hr'],
        ['Pipelayer - 20,000 lb Sideboom', 'JD 650J, Cat D4 or Equivalent', 92.19, 'hr'],
        ['Pipelayer - 40,000 lb Sideboom', 'JD 700J, Cat D5, Cat 561 or Equivalent', 118.17, 'hr'],
        ['Pipelayer - 60,000 lb Sideboom', 'JD 750J, Cat D6M LGP or Equivalent', 154.55, 'hr'],
        ['Pipelayer - 80,000 lb Sideboom', '', 169.49, 'hr'],
        ['Pipelayer - 100,000 lb Sideboom', '', 181.79, 'hr'],
        ['Pipelayer - 140,000 lb Sideboom', '', 282.29, 'hr'],
    ],
    'Crawler Equipment': [
        ['Crawler LGP Carrier (Not in Tack Rig Config) - <10k lbs Cap.', '', 99.55, 'hr'],
        ['Crawler LGP Carrier (Not in Tack Rig Config) - 13k-20k lbs Cap.', '', 147.32, 'hr'],
        ['Crawler LGP Carrier (Not in Tack Rig Config) - 21k to 30k lbs Cap.', '', 180.36, 'hr'],
        ['Attachment - Crane Attachment (for Crawler LGP 21K-30K)', '', 150.00, 'day'],
        ['Crawler LGP Carrier (Not in Tack Rig Config) - 31k to 45k lbs Cap.', '', 211.61, 'hr'],
    ],
    'Pipeline Equipment': [
        ['Bending Machine - 6" - 20"', 'Includes Bending Set & Mandrel', 872.73, 'day'],
        ['Bending Machine - 16" - 30"', 'Includes Bending Set & Mandrel', 1387.50, 'day'],
        ['Bending Machine - 22" - 36"', 'Includes Bending Set & Mandrel', 1665.00, 'day'],
        ['Lower-in Cradles - 6" - 20" Pipe', '', 184.00, 'day'],
        ['Lower-in Cradles - 12" - 24" Pipe', '', 276.00, 'day'],
        ['Lower-in Cradles - 24" - 36" Pipe', '', 478.00, 'day'],
        ['Lower-in Cradles - 50" - 60" Pipe', '', 1280.00, 'day'],
        ['Lower-in Belts - 36" - 42" Pipe', 'Includes 8 hole Headiron', 236.00, 'day'],
        ['Lower-in Belts - 43" - 48" Pipe', '', 312.00, 'day'],
        ['Weld Support Tractor', 'Welding Support Tractors: Dozer Based: Tack Rig w/ 2 ea 200 Amp Welding Machines', 98.88, 'hr'],
        ['Welding Support Tractors: LGP Carrier w/ 4 x Welding Machines', 'T8 Prinoth w/ Vanguard, Weld Deck and 4 XMT Welding Machine', 171.43, 'hr'],
        ['Welding Support Tractors: LGP Carrier w/ Telescoping Crane Support + 4 x Welding Machines', 'T12 Prinoth w/ Tiger 1069 Telescoping Crane, Vanguard Weld Deck and 4 x Welding Machine', 173.66, 'hr'],
        ['Welding Support Tractors: Pipelayer Welding Support Tractor (a/k/a Shack Tractor)', 'Pipelayer (571/572) w/ Jib, Bottle Rack and Vanguard Weld Deck. (No Welding Machines Included)', 217.05, 'hr'],
        ['Tack Rig w/ 2 x 200 Amp Welding Machines', 'Includes Normal Welding Rods, Grinding Disks, & Oxygen/Acetylene; Truck Mounted is Equivalent', 129.60, 'hr'],
        ['Tack Rig w/ 4 x 350 Amp Welding Machines (including Soft Welding Shack)', 'T12 Prinoth w/ Tiger 1069 Telescoping Crane, Vanguard Weld Deck and 4 XMT Welding Machine', 218.50, 'hr'],
        ['Welding Shelter - Metal Half Shack w/ Canvas Sides', '', 125.00, 'day'],
        ['Weld Deck', 'Applicable for pipelayer or LGP Carrier weld support tractor; Includes power for up to four welding or induction units; Welding machines included', 1250.00, 'day'],
        ['Poly Fusion Machine (2" to 4")', '', 188.00, 'day'],
        ['Poly Fusion Machine (6" to 10")', '', 612.78, 'day'],
        ['Poly Fusion Machine (12" to 18")', '', 325.71, 'day'],
        ['Poly Fusion Machine (18" to 23")', '', 2538.57, 'day'],
        ['Poly Fusion Machine (24")', 'Trakstar', 2663.24, 'day'],
        ['Welding Machine - 200 Amp', '', 165.71, 'day'],
        ['Welding Machine - 300 Amp', '', 238.99, 'day'],
        ['Poly/FlexSteel - A-Frame Set', '', 160.00, 'day'],
        ['FlexSteel Field Kit with Cradles', '', 200.00, 'day'],
        ['Swaging Equipment (FlexSteel) - 2"-6"', '', 400.00, 'day'],
        ['Swaging Equipment (FlexSteel) - 6"-8"', '', 616.67, 'day'],
        ['Plasma Cutter - 60 Amp', '', 110.66, 'day'],
    ],
    'Air Compressors / Painting Equipment': [
        ['Air Compressors - < 10 HP', '', 140.95, 'day'],
        ['Air Compressors - 185 CFM', '', 218.32, 'day'],
        ['Air Compressors - 250 CFM', '', 272.06, 'day'],
        ['Air Compressors - 375 CFM', '', 346.84, 'day'],
        ['Air Compressors - 300 CFM High Pressure', '', 333.26, 'day'],
        ['Air Compressors - 400 CFM High Pressure', '', 544.29, 'day'],
        ['Air Compressors - 900 CFM High Pressure', '', 1500.00, 'day'],
        ['Air Compressors - 1600 CFM High Pressure', '', 3345.43, 'day'],
        ['Sandblast & Paint Rig - Trailer w/ 185cfm A/Comp, 300lb blast pot, hoses, regular paint pot w/agitate & liner. Painter truck is billed separately.', 'Paint, Thinner, Plastic & Other Consumables are Cost plus Mark Up', 76.25, 'hr'],
        ['Sandblast & Paint Rig - Trailer w/ 375cfm A/Comp, 600lb blast pot, hoses, regular paint pot w/agitate & liner. Painter truck is billed separately.', 'Paint, Thinner, Plastic & Other Consumables are Cost plus Mark Up', 84.29, 'hr'],
        ['Painting Equipment', 'Includes Pot, Gun & Hoses Less Paint. Requires Air Compressor', 143.62, 'day'],
        ['Sandblasting Equipment', 'Includes Pot, Hoses, Hood Less sand. Requires Air Compressor', 285.00, 'day'],
        ['Dew Point Tester', '', 86.67, 'day'],
    ],
    'Generators / Water Pumps': [
        ['Light Plant - 6.0 - 7.5 KW', 'Includes Light Tower or Equivalent', 121.51, 'day'],
        ['Generator - <5.0 KW', '', 72.57, 'day'],
        ['Generator - 5.0 to 7.5 KW', '', 74.49, 'day'],
        ['Generator - 7.5 to 10 KW', '', 138.14, 'day'],
        ['Generator - 25kva', '', 209.25, 'day'],
        ['Generator - 30 KW', '', 214.93, 'day'],
        ['Centrifugal Pump - 2"', 'Hoses are included', 120.00, 'day'],
        ['Centrifugal Pump - 3"', 'Hoses are included', 183.33, 'day'],
        ['Centrifugal Pump - 4"', 'Hoses are included', 236.25, 'day'],
        ['Centrifugal Pump - 6"', 'Hoses are included', 282.15, 'day'],
        ['Diaphragm Pump - 2"', 'Hoses are included', 150.00, 'day'],
        ['Ditch Pump - 4"', '', 169.31, 'day'],
        ['Ditch Pump - 6"', '', 282.35, 'day'],
        ['Hydraulic Pump', 'Hytorc or Equivalent', 282.15, 'day'],
    ],
    'Hydro Testing Equipment': [
        ['Flow Meters - 1 - 2 Inch', '', 66.67, 'day'],
        ['Digital Pressure / Temperature Recorder', '', 231.25, 'day'],
        ['Digital Pressure Gauge', '', 90.63, 'day'],
        ['Chart Recorder', '', 93.75, 'day'],
        ['Pressure Test Pump - 5000 psi', '', 187.50, 'day'],
        ['Hydro Test Trailer', 'Includes hoses, fittings, blind flanges, valves 1" & below', 451.43, 'day'],
    ],
    'Cathodic Protection Equipment': [
        ['Truck - 80 BBL Vac Truck', '', 75.37, 'hr'],
        ['1/2 Ton pickup - tooled for CP Tech Services', '', 35.00, 'hr'],
        ['1 Ton pickup - tooled for CP Construction', '', 40.00, 'hr'],
        ['Truck - 2 Ton Flatbed - with CP Tools', '', 53.00, 'hr'],
        ['Auger Truck', '', 100.00, 'hr'],
        ['GPS Mapping / Survey Equipment', '', 200.00, 'day'],
        ['Allergo Data Meter', '', 100.00, 'day'],
        ['GPS synchronized current interrupter', '', 65.00, 'day'],
        ['UTV / 4x4 side by side / 8x8 amphibious', '', 200.00, 'day'],
        ['Drilling Rig', '', 360.00, 'hr'],
        ['Hydraulic Coke Pump', '', 35.00, 'hr'],
    ],
    'Farm & Grounds Equipment': [
        ['Chain Saw', '', 106.84, 'day'],
        ['Chipper - < 6"', '', 415.69, 'day'],
        ['Chipper - 6" to 10"', '', 540.19, 'day'],
        ['Chipper - 10" to 12"', '', 683.85, 'day'],
        ['Farm Implement', '', 105.21, 'day'],
        ['Farm Tractor - 40 HP', '', 36.83, 'hr'],
        ["Attachment - 6' Brush Hog (40 HP)", '', 150.00, 'day'],
        ["Attachment - 8' Brush Hog (40 HP)", '', 182.20, 'day'],
        ["Attachment - 8' Disc (40 HP)", '', 265.00, 'day'],
        ['Farm Tractor - 70 HP', '', 45.12, 'hr'],
        ["Attachment - 8' Brush Hog (70 HP)", '', 217.50, 'day'],
        ["Attachment - 15' Batwing (70 HP)", '', 150.00, 'day'],
        ["Attachment - 8' Disc (70 HP)", '', 182.20, 'day'],
        ["Attachment - 16' Disc (70 HP)", '', 777.86, 'day'],
        ['Farm Tractor - 110 HP', '', 116.61, 'hr'],
        ["Attachment - 15' Batwing (110 HP)", '', 315.00, 'day'],
        ["Attachment - 16' Disc (110 HP)", '', 678.00, 'day'],
        ['Lawnmower - Push', '', 42.14, 'day'],
        ['Lawnmower - Riding', '', 184.29, 'day'],
        ['Straw Blower Attachment', '', 345.00, 'day'],
        ['Weedeater', '', 72.86, 'day'],
    ],
    'Electrical Equipment': [
        ['Rigid 300 Pipe Threading Machine', '', 277.71, 'day'],
        ['Hydraulic Knock Out Set 1/2" to 4"', '', 171.00, 'day'],
        ['Mechanical Conduit Bender 1/2" to 1 1/2"', '', 316.00, 'day'],
        ['Hydraulic Conduit Bender 2" to 4"', '', 138.70, 'day'],
        ['Electric Conduit Bender 1/2" to 2"', '', 258.55, 'day'],
        ['Electric/Hydraulic Conduit Bender 2" to 4"', '', 298.67, 'day'],
        ['Hydraulic Crimping Tool (die-less)', '', 382.80, 'day'],
        ['Cable Tray Roller Kit', '', 100.00, 'day'],
        ['Cable Pulling Tugger', '', 125.00, 'day'],
        ['Cable Pulling Reel Jacks', '', 32.40, 'day'],
        ['DC Hi-pot Machine (15KV)', '', 145.83, 'day'],
        ['24 VDC Power Supply', '', 20.00, 'day'],
        ['Electrical Testing / Grounding Testing', 'Includes 1000V Megger, Grounding Grid Tester, Fluke Digital Multimeter', 86.67, 'day'],
        ['Instrumentation Testing', 'Jofra Temperature Calibrator, Transmation (4-20 simulator), Hart Communicator, 0-3000 psi Hand Pump, Pneumatic Test Pump', 273.33, 'day'],
        ["Truck - Bucket Truck with Utility Bed, Tools - 55-60' Reach", '', 69.03, 'hr'],
        ["Truck - Bucket Truck with Utility Bed, Tools - 60-85' Reach", '', 109.78, 'hr'],
        ['Attachment Digger Auger (for Skid Steer) - 18-30"', '', 225.00, 'day'],
        ['Attachment Digger Auger (for Skid Steer) - 36-44"', '', 375.00, 'day'],
    ],
    'Misc Equipment': [
        ['Bending Shoe & Bracket (4" - 8")', '', 100.00, 'day'],
        ['Pipe Roller', '', 100.00, 'ea'],
        ['Spreader Bar (Rigging etc)', '50 ton and below', 133.33, 'day'],
        ["Hose - Additional 50' Compressor Hose", '', 20.00, 'day'],
        ['Heating System - 35kw', 'IHS Preheat AC Package (Includes Heating Blanket)', 321.43, 'day'],
        ['Jersey/Concrete Barrier', '', 3.50, 'day'],
        ['Lifting Bags - 24" to 29"', '', 50.00, 'day'],
        ['Broom - 8 ft Power Sweeper', '', 416.70, 'day'],
        ['Pressure Washer - Portable Cart Mounted', 'Cat 6 Cart or equivalent', 117.43, 'day'],
        ['Pressure Washer - 1 Gun, Trailer Mounted, Heated w/ Water Tank', '', 204.33, 'day'],
        ['Port-a-Cool Evaporative Cooler', '36" Evaporative Cooler', 86.25, 'day'],
        ["Road Plate - 10' x 12'", '', 33.00, 'day'],
        ['Skids', 'Price is Per Skid Per Day. Material is Considered Rented ONLY', 1.33, 'day'],
        ['Tensile Tester (Iron Horse)', '', 312.50, 'day'],
        ['Trench Box', 'Cat 6 Trench or equivalent', 266.67, 'day'],
        ['Troweling Machine', '', 185.71, 'day'],
        ['Vactron - Trailer Mounted', 'Cat 6 Trailer or equivalent', 259.96, 'day'],
        ['Vactron - Skid Mounted', 'Cat 6 Skid or equivalent', 416.67, 'day'],
        ['Mats - 4\' x 16\' x 6"', 'Digging Mats; Extended Period Rentals Must be Requested in Advance.', 26.10, 'day'],
        ['Mats - 8\' x 16\' x 4"', 'Laminated Mats; Extended Period Rentals Must be Requested in Advance.', 27.30, 'day'],
        ['36x60 Gang Box', '', 35.00, 'day'],
        ['GPS Equipment (Sub-Meter)', 'Cat 6 Equipment or equivalent', 83.33, 'day'],
        ['GPS Equipment (RTK/Static)', 'Cat 6 Equipment or equivalent', 250.00, 'day'],
        ['Guillotine Saw, 6"-18"', 'Pneumatic including consumables', 320.00, 'day'],
        ['535 Rigid Threader 1/2"- 2"', '', 96.82, 'day'],
        ['700 Rigid Threader up to 2"', '', 96.82, 'day'],
        ['1224 Rigid Threader 1/2"- 4"', '', 149.80, 'day'],
        ['Victaulic Groover', 'Roller Groover Hydraulic 12"', 100.00, 'day'],
        ['Rig Heater', '', 123.52, 'day'],
    ],
    'Tool Vans': [
        ["Trailer - 12' - 24' Enclosed Tool Trailer", 'Includes basic hand tools and storage for consumables only', 397.13, 'day'],
        ["Tool Van - 40' - 53' Trailer", 'Includes basic hand tools and storage for consumables only', 1246.21, 'day'],
        ["Tool Van - 40' - 53' Trailer (excludes tools)", '', 206.72, 'day'],
    ],
    'Small Tools': [
        ['Pipeline Locator (Metrotech)', '', 96.00, 'day'],
        ['CO Monitor and Air Quality Alarm', 'Required for Situations Using a Supplied Air Source', 50.00, 'day'],
        ['Beveling Band w/ Crawler (14" to 26")', '', 133.50, 'day'],
        ['Beveling Band w/ Crawler (28" to 36")', '', 159.71, 'day'],
        ['Beveling Band w/ Crawler (38" to 42")', '', 182.50, 'day'],
        ['Beveling Band w/ Crawler (43" to 48")', '', 205.29, 'day'],
        ['Beveling Band w/ Motorized Crawler (38" to 42")', 'Includes Beveling Band, Motorized Kit, and Crawler', 348.00, 'day'],
        ['Beveling Band w/ Motorized Crawler (43" to 48")', 'Includes Beveling Band, Motorized Kit, and Crawler', 432.00, 'day'],
        ['Beveling Machine - 2" to 4"', '', 55.70, 'day'],
        ['Beveling Machine - 6" to 12"', '', 69.70, 'day'],
        ['Beveling Machine - 14" to 26"', '', 135.70, 'day'],
        ['Beveling Machine - 28" to 36"', '', 225.70, 'day'],
        ['Beveling Machine - 38" to 42"', '', 480.80, 'day'],
        ['Beveling Machine - 43" to 48"', '', 533.33, 'day'],
        ['Pipe Clamps (2" to 6")', '', 45.00, 'day'],
        ['Pipe Clamps (8" to 12")', '', 50.91, 'day'],
        ['Pipe Clamps (14" to 20")', '', 75.00, 'day'],
        ['Pipe Clamps (22" to 36")', '', 122.00, 'day'],
        ['Pipe Clamps (38" to 42")', 'KC External Pipe Clamps', 323.00, 'day'],
        ['Pipe Clamps (43" to 48")', 'KC External Pipe Clamps', 374.00, 'day'],
        ['Holiday Detector', '780 Spy Includes Jeep Spring', 258.00, 'day'],
        ['Concrete Vibrator', '', 150.00, 'day'],
        ['Hand Held Cut-off Saw', 'Concrete Saw, Demo Saw, or Equivalent', 756.08, 'day'],
        ['Jack Hammer', '', 119.31, 'day'],
        ['Portable Cement Mixer', '', 118.93, 'day'],
        ['Air Saw (less blades)', '', 68.80, 'day'],
        ['Torque Wrench - Air', '', 267.20, 'day'],
        ['Torque Wrench - Hydraulic', '', 354.80, 'day'],
        ['Brazing Pin Unit', 'Corrosion Control', 466.67, 'day'],
        ['4 Gas Monitor', '', 8.00, 'day'],
    ],
};

// ── Which equipment burns fuel ──
// The fuel surcharge is an adder meant to recover diesel cost, so it only
// earns its keep on equipment that actually burns fuel. A towed trailer, a
// hammer hanging off an excavator, or a set of pipe clamps consumes none —
// billing a diesel adder on those is what a client's auditor challenges
// first. Every equipment row therefore carries a `fuel` flag, and only
// flagged rows land in the surcharge base (billingFuel.fuelEligible).
//
// The flag below is the company-wide SEED. It is a judgment call per row and
// is editable per job in Rates & Rules, which is where a negotiated
// classification belongs — a client who agrees that attachments ride the
// surcharge just ticks them back on for that job.
//
// The rule: does this line item have an engine of its own that consumes
// diesel or gasoline? Self-propelled machines, engine-driven welders,
// towable compressors/generators/light plants and their own power packs do.
// Attachments, rigging, towed boxes, hand tools and instruments do not.

// Sections where nothing carries its own engine, bar the exceptions below.
const NO_FUEL_SECTIONS = new Set([
    'Trailers', 'Hydro Testing Equipment', 'Electrical Equipment', 'Misc Equipment', 'Tool Vans', 'Small Tools',
]);

// Rows inside a NO_FUEL_SECTIONS section that DO run an engine.
const FUELED_ROWS = new Set([
    "Trailer - 16' Open Side Cooling Trailer",       // Port-a-Cool + generator on the deck
    'Pressure Test Pump - 5000 psi',
    "Truck - Bucket Truck with Utility Bed, Tools - 55-60' Reach",
    "Truck - Bucket Truck with Utility Bed, Tools - 60-85' Reach",
    'Broom - 8 ft Power Sweeper',
    'Pressure Washer - Portable Cart Mounted',
    'Pressure Washer - 1 Gun, Trailer Mounted, Heated w/ Water Tank',
    'Troweling Machine',
    'Vactron - Trailer Mounted',
    'Vactron - Skid Mounted',
    'Rig Heater',                                    // diesel-fired
    'Hand Held Cut-off Saw',
    'Portable Cement Mixer',
]);

// Rows inside an otherwise fuel-burning section that carry no engine — they
// hang off, get towed by, or plug into something else that does. A name
// repeated inside a section (the vacuum pipe lifts, the padding buckets)
// is covered once here.
const NO_FUEL_ROWS = new Set([
    // Excavators — every attachment runs off the host machine's hydraulics
    'Attachment - Backhoe Hammer',
    'Attachment - Excavator 2,000 lb Hammer',
    'Attachment - Excavator 3,500 lb Hammer',
    'Attachment - Excavator 6,500 lb Hammer',
    'Attachment - Excavator 7,500 lb Hammer',
    'Attachment - Excavator 10,000 lb Hammer',
    'Attachment - Vacuum Pipe Lift',
    'Attachment - Excavator Vacuum Pipe Lift',
    'Attachment - Excavator Padding Bucket',
    // Front End Loaders / Crawler — skid steer and carrier attachments
    'Attachment - Post Hole Digger (for Skid Steer Mini Loader)',
    'Attachment - Broom (for Skid Steer Mini Loader)',
    'Attachment - Mower Deck (for Skid Steer Mini Loader)',
    'Attachment - Grapple (for Skid Steer Mini Loader)',
    'Attachment - Auger w/Bit (for Skid Steer)',
    'Attachment - Mulching Head (for Skid Steer)',
    'Attachment - Crane Attachment (for Crawler LGP 21K-30K)',
    // Pipeline Equipment — lowering-in gear, shelters and field kits
    'Lower-in Cradles - 6" - 20" Pipe',
    'Lower-in Cradles - 12" - 24" Pipe',
    'Lower-in Cradles - 24" - 36" Pipe',
    'Lower-in Cradles - 50" - 60" Pipe',
    'Lower-in Belts - 36" - 42" Pipe',
    'Lower-in Belts - 43" - 48" Pipe',
    'Welding Shelter - Metal Half Shack w/ Canvas Sides',
    'Poly/FlexSteel - A-Frame Set',
    'FlexSteel Field Kit with Cradles',
    'Swaging Equipment (FlexSteel) - 2"-6"',
    'Swaging Equipment (FlexSteel) - 6"-8"',
    'Plasma Cutter - 60 Amp',
    // Air Compressors / Painting — the pots and guns need a compressor, and
    // the compressor is the row that carries the engine
    'Painting Equipment',
    'Sandblasting Equipment',
    'Dew Point Tester',
    // Generators / Water Pumps
    'Hydraulic Pump',                                // Hytorc — electric pack
    // Cathodic Protection — survey and metering instruments
    'GPS Mapping / Survey Equipment',
    'Allergo Data Meter',
    'GPS synchronized current interrupter',
    // Farm & Grounds — towed implements
    'Farm Implement',
    "Attachment - 6' Brush Hog (40 HP)",
    "Attachment - 8' Brush Hog (40 HP)",
    "Attachment - 8' Disc (40 HP)",
    "Attachment - 8' Brush Hog (70 HP)",
    "Attachment - 15' Batwing (70 HP)",
    "Attachment - 8' Disc (70 HP)",
    "Attachment - 16' Disc (70 HP)",
    "Attachment - 15' Batwing (110 HP)",
    "Attachment - 16' Disc (110 HP)",
    'Straw Blower Attachment',
]);

// Seed fuel flag for an equipment row.
const burnsFuel = (sec, name) => (NO_FUEL_SECTIONS.has(sec)
    ? FUELED_ROWS.has(name)
    : !NO_FUEL_ROWS.has(name));

// ── Provisions (the "rules") ──
const LABOR_RULES = [
    "Billable Labor rates are based on cost plus a fee of 1.633 x actual employee base wage to cover wages, payroll taxes, workers' compensation insurance, general liability insurance, basic OSHA safety training, operator qualification assessments, overhead, and profit.",
    "Overtime rates shall apply to all hours worked in excess of forty (40) hours per week, and recognized holidays. Recognized holidays include: New Year's Day, Good Friday, Independence Day (July 4th), Thanksgiving Day, Christmas Eve, Christmas Day, and any additional holidays observed by Customer for its own employees.",
    "Weather Delays and Work Stoppages: Partial workdays shall be billed as follows: - Inability to commence scheduled work due to conditions beyond Contractor's control: minimum 2 hours per person - Work commenced but suspended by Customer within 4 hours: minimum 4 hours per person - Work exceeding 4 hours: the greater of actual hours worked or 5 hours per person minimum",
    "All labor rates apply portal-to-portal, measured from Contractor's designated facility to the job site and return.",
    'Per Diem: Personnel requiring overnight accommodations shall receive subsistence at a rate of $175. Prevailing market conditions may necessitate adjustment to this rate. All per diem charges constitute reimbursements subject to limitations under Treasury Regulation Section 274(n).',
    'Personnel assigned to projects requiring a minimum of five (5) consecutive workdays, and otherwise eligible for per diem, shall receive per diem compensation for the sixth and seventh days regardless of hours worked, provided the project assignment',
    'Third-party services and materials shall be invoiced at actual cost plus a twelve percent (12%) administrative and handling fee.',
    'Staffing Requirements: Project sites utilizing more than ten (10) personnel on a continuous basis shall require an on-site Project Administrative Support Specialist. Sites utilizing more than twenty-five (25) personnel shall additionally require a Project Coordinator or Office Manager.',
    'Applicable sales tax, gross receipts tax, or equivalent assessments shall be charged in addition to the rates specified herein.',
    "This Labor Rate Schedule identifies the Contractor's target base wage assumptions for estimating purposes. Actual wages paid may vary from the target wage assumptions based on labor market conditions, employee availability, geographic location, project duration, schedule requirements, and other workforce-related factors. Actual wages may be either higher or lower than the target wage assumptions provided in the Labor Rate Schedule. Labor will be billed using the actual wage paid to each employee in accordance with the formulas defined above. Should market conditions require wage premiums, retention incentives, per diem, travel allowances, shift differentials, or other compensation necessary to attract and retain qualified personnel, such costs shall be considered part of the employee's actual wage and shall be billable in accordance with the established labor rate formula.",
];

const EQUIP_RULES = [
    'Equipment rates are inclusive of maintenance, repair, fuel, oil, lube, taxes, insurance, depreciation, license fees, overhead and profit. Equipment rates are on a straight-time basis and are exclusive of operating labor. Equipment provided will be as described in the rate schedule or equivalent.',
    "A minimum daily charge for all equipment assigned shall be five (5) hours or one half (1/2) of listed daily rates, whichever is greater. All equipment onsite shall receive at least the minimal daily charge for standby. Unnecessary machines must be ordered to be hauled off at Customer's expense. Rainouts are handled per policy listed under labor provisions.",
    'Service time for fueling and greasing of equipment shall be considered work time.',
    'Rates apply for all time including mobilization, demobilization and move-arounds. Cost for third-party hauling and/or extra permits shall be charged at cost plus twelve percent (12%) administrative and handling fee.',
    'Equipment rates shall apply to all Contractor-sourced equipment for which an equipment rate is provided in this rate sheet, regardless of how contractor chooses to source such equipment or whether or not such equipment is owned, leased or rented from a third-party. With respect to specialty equipment for which an equipment rate is not provided in this rate sheet, such equipment shall be provided from third-parties and shall be charged at cost plus twelve percent (12%), plus fuel for such specialty equipment shall be charged at cost.',
    'Equipment rates are portal to portal.',
    'All equipment hours shall match those of the operators onsite.',
    'Sales tax, if applicable, shall be charged in addition to the above rates.',
    'The following labor classifications will require a company vehicle to be provided and billed at the corresponding rates: Senior Management, Superintendents (General, A/B, Assistant), Foreman (General, Craft, Labor), Safety Manager, Safety Specialists, Project Coordinator, and Project Admin Support Specialist (or equivalent). Equipment Operators will require either a company supplied truck or a contract truck rental according to their specific equipment.',
    'Equipment rates are quoted as daily rates and assume single ten (10) hour shift use. Should the average shift regularly exceed ten (10) hours in length additional charges will apply.',
    'Rates are based on $3.75 per U.S. Gulf Coast gallon of highway non-dyed diesel. Please see included fuel surcharge index for price escalation over $3.75.',
];

const FUEL_RULES = [
    'Fuel Surcharge shall engage on 1st day of calendar month immediately following end of quarter year period (13 weeks) with US Gulf Coast Diesel Quarterly Average Pricing above established Threshold Pricing Level.',
    'Fuel Surcharge percentage shall adjust on 1st day of calendar month following conclusion of each quarter year period (13 weeks) based on current US Gulf Coast quarterly average diesel pricing.',
    '"Threshold Pricing Level" for fuel surcharge engagement shall be defined as $3.75 USD per US Gallon.',
    '"US Gulf Coast Diesel Quarterly Average Pricing" shall be defined as the average of the 13 individual PADD 3 weekly reports of prior quarter\'s on-highway diesel price as officially posted on the U.S. Energy Information Administration\'s website (www.eia.gov).',
    'The percentage in column "Equipment % Adder" shall apply ONLY to equipment subtotals on each invoice and not labor, per diem, or materials.',
    'No rebate or credit shall apply for average On-Highway diesel prices below $3.75.',
];

// ── Fuel Surcharge Index ──
// [EIA Gulf Coast price threshold, equipment % adder]
const FUEL = [
    ['< $3.75', '0.0%'],
    ['$3.80', '0.7%'],
    ['$3.85', '1.3%'],
    ['$3.90', '2.0%'],
    ['$3.95', '2.7%'],
    ['$4.00', '3.3%'],
    ['$4.05', '4.0%'],
    ['$4.10', '4.7%'],
    ['$4.15', '5.3%'],
    ['$4.20', '6.0%'],
    ['$4.25', '6.7%'],
    ['> $4.25', 'Additional 0.67% per $0.05 of increase'],
];

// Normalized default rows with content-hashed ids. Duplicate name+notes pairs
// inside a section (none today) get an occurrence counter so ids stay unique.
const buildDefaults = () => {
    const seen = {};
    const rid = (parts) => {
        const base = parts.join('|');
        seen[base] = (seen[base] || 0) + 1;
        return 'rt_' + hash(base + '#' + seen[base]);
    };
    const labor = [];
    Object.entries(LABOR).forEach(([sec, rows]) => rows.forEach(([name, wage, note]) => {
        labor.push({ id: rid(['L', sec, name, note || '']), t: 'L', sec, name, note: note || '', wage });
    }));
    const equip = [];
    Object.entries(EQUIP).forEach(([sec, rows]) => rows.forEach(([name, notes, rate, uom]) => {
        equip.push({ id: rid(['E', sec, name, notes]), t: 'E', sec, name, notes, rate, uom, fu: burnsFuel(sec, name) });
    }));
    const fuel = FUEL.map(([name, adder]) => ({ id: rid(['F', '', name, '']), t: 'F', name, adder }));
    const rules = [];
    Object.entries({ labor: LABOR_RULES, equip: EQUIP_RULES, fuel: FUEL_RULES }).forEach(([sec, list]) =>
        list.forEach((text, i) => rules.push({ id: `ru_${sec}_${i + 1}`, sec, text })));
    return { labor, equip, fuel, rules };
};

export const DEFAULT_SCHEDULE = buildDefaults();

export const LABOR_SECTIONS = Object.keys(LABOR);
export const EQUIP_SECTIONS = Object.keys(EQUIP);
